import React, { useEffect, useState } from 'react';
import { Copy, X } from 'lucide-react';
import { formBuilderApi } from '../api';
import { FORM_TAG_LABELS, FormTag, Option } from '../types';
import { iconButton, primaryButton, secondaryButton } from '../ui';
import { inputSmall } from './panelParts';

export type DuplicateSelection = { name: string; sensor_type: string; category: string; tags: FormTag[] };

type DuplicateDialogProps = {
  /** Values of the form being copied; the selects start on these. */
  initial: { name: string; sensor_type?: string | null; category?: string | null; tags?: FormTag[] };
  busy: boolean;
  onClose: () => void;
  onSubmit: (selection: DuplicateSelection) => void;
};

const TAGS = Object.keys(FORM_TAG_LABELS) as FormTag[];

/**
 * Sensor type, category and calibration type can't be changed once a form exists, so the copy
 * is where they are chosen. They default to the source form's values.
 */
export default function DuplicateDialog({ initial, busy, onClose, onSubmit }: DuplicateDialogProps) {
  const [name, setName] = useState(initial.name);
  const [sensorType, setSensorType] = useState(initial.sensor_type || '');
  const [category, setCategory] = useState(initial.category || '');
  const [tag, setTag] = useState<FormTag | ''>(initial.tags?.[0] || '');
  const [sensorOptions, setSensorOptions] = useState<Option[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    formBuilderApi.sensorTypeOptions()
      .then(setSensorOptions)
      .catch((e) => setError(e?.response?.data?.detail || e?.message || 'Failed to load sensor types'))
      .finally(() => setLoading(false));
  }, []);

  // Categories depend on the sensor type. Keep the current category when it is still offered;
  // otherwise clear it so the user has to pick one.
  useEffect(() => {
    setCategoryOptions([]);
    if (!sensorType) return;
    let cancelled = false;
    setCategoriesLoading(true);
    formBuilderApi.categoryOptions(sensorType)
      .then((options) => {
        if (cancelled) return;
        setCategoryOptions(options);
        setCategory((current) => (options.some((o) => o.value === current) ? current : ''));
      })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || e?.message || 'Failed to load categories'); })
      .finally(() => { if (!cancelled) setCategoriesLoading(false); });
    return () => { cancelled = true; };
  }, [sensorType]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  const ready = !!name.trim() && !!sensorType && !!category && !!tag;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/30 p-4 backdrop-blur-[2px]">
      <div role="dialog" aria-modal="true" aria-labelledby="duplicate-form-title" className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-5 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 id="duplicate-form-title" className="text-sm font-semibold text-slate-900">Duplicate form</h2>
            <p className="mt-1 text-[11px] leading-4 text-slate-500">
              Creates a new draft with the same steps, fields, logic and data-source configuration. Sensor type, category and calibration type can’t be changed after this, so choose them now.
            </p>
          </div>
          <button type="button" className={iconButton} onClick={() => onClose()} aria-label="Close duplicate dialog"><X className="h-3.5 w-3.5" /></button>
        </div>

        <div className="mt-4 space-y-3">
          <label className="block">
            <span className="mb-1 block text-[10px] font-medium text-slate-600">Form name</span>
            <input autoFocus className={inputSmall()} value={name} onChange={(e) => setName(e.target.value)} />
          </label>

          <label className="block">
            <span className="mb-1 block text-[10px] font-medium text-slate-600">Sensor type</span>
            <select className={inputSmall()} value={sensorType} disabled={loading} onChange={(e) => setSensorType(e.target.value)}>
              <option value="">{loading ? 'Loading sensor types…' : 'Select a sensor type'}</option>
              {sensorOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>

          <label className="block">
            <span className="mb-1 block text-[10px] font-medium text-slate-600">Category</span>
            <select className={inputSmall()} value={category} disabled={!sensorType || categoriesLoading} onChange={(e) => setCategory(e.target.value)}>
              <option value="">{!sensorType ? 'Select a sensor type first' : categoriesLoading ? 'Loading categories…' : 'Select a category'}</option>
              {categoryOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>

          <label className="block">
            <span className="mb-1 block text-[10px] font-medium text-slate-600">Calibration type</span>
            <select className={inputSmall()} value={tag} onChange={(e) => setTag(e.target.value as FormTag)}>
              <option value="">Select a calibration type</option>
              {TAGS.map((t) => <option key={t} value={t}>{FORM_TAG_LABELS[t]}</option>)}
            </select>
          </label>

          {error && <p role="alert" className="text-[11px] text-red-600">{error}</p>}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className={secondaryButton} onClick={() => onClose()} disabled={busy}>Cancel</button>
          <button type="button" className={primaryButton} disabled={busy || !ready}
            onClick={() => ready && onSubmit({ name: name.trim(), sensor_type: sensorType, category, tags: [tag as FormTag] })}>
            <Copy className="h-3.5 w-3.5" /> {busy ? 'Duplicating…' : 'Create duplicate'}
          </button>
        </div>
      </div>
    </div>
  );
}
